import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Collapse,
  Fab,
  FormControl,
  FormControlLabel,
  makeStyles,
  Paper,
  Radio,
  RadioGroup,
  RadioGroupProps,
  Tooltip,
  Typography,
} from "@material-ui/core";
import FilterListIcon from "@material-ui/icons/FilterList";
import AccountSearch from "components/AccountSearch";
import { SearchContainer } from "../styled";

import { MailboxAccountsQuery } from "@dashboard-v3/api";

export type SearchCriteria = Omit<MailboxAccountsQuery, "limit" | "page">;

interface Props {
  values: SearchCriteria;
  onSearch: (criteria: SearchCriteria) => void;
}

const statuses: Array<MailboxAccountsQuery["status"]> = [
  "EXECUTING_STATUS",
  "FINISHED_STATUS",
  "DELETED_STATUS",
  "ERROR_STATUS",
];

type SearchState = {
  emailAccount: string;
  status: MailboxAccountsQuery["status"];
  showStatusFilter: boolean;
};

export default function Search(props: Props) {
  const { t } = useTranslation("stats");
  const classes = useStyles();
  const [searchState, setSearchState] = useState<SearchState>(initSearchState);

  function updateSearchState(update: Partial<SearchState>) {
    setSearchState(prev => ({ ...prev, ...update }));
  }

  function handleShowFilters() {
    updateSearchState({
      showStatusFilter: !searchState.showStatusFilter,
    });
  }

  const handleSearch = (emailAccount: string) => {
    props.onSearch({ ...searchState, emailAccount });
  };

  const onFilterChange: RadioGroupProps["onChange"] = e => {
    const { value } = e.target;
    updateSearchState({
      status: value as MailboxAccountsQuery["status"],
    });
  };

  return (
    <>
      <div className={classes.mainContainer}>
        <SearchContainer>
          <AccountSearch
            placeholder={t("accountSearch.emailAccount.label")}
            onSearch={handleSearch}
            onClear={() => updateSearchState({ emailAccount: "" })}
          />
        </SearchContainer>

        <Tooltip title={t("accountSearch.showFilters")} placement="right">
          <Fab
            size="small"
            className={classes.filterBtn}
            onClick={handleShowFilters}
          >
            <FilterListIcon fontSize="small" />
          </Fab>
        </Tooltip>
      </div>
      <Collapse in={searchState.showStatusFilter}>
        <Paper elevation={0} className={classes.filterPanel}>
          <FormControl fullWidth>
            <Typography
              color="textSecondary"
              variant="body2"
              className={classes.statusFilterLabel}
            >
              {t("accountSearch.status.label")}:
            </Typography>
            <RadioGroup
              row
              value={searchState.status}
              onChange={onFilterChange}
            >
              <FormControlLabel
                control={<Radio color="primary" size="small" />}
                label={t("accountSearch.filterLabel.ALL")}
                value=""
              />
              {statuses.map(status => (
                <FormControlLabel
                  key={status}
                  control={<Radio color="primary" size="small" />}
                  label={t(`accountSearch.filterLabel.${status}`)}
                  value={status}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </Paper>
      </Collapse>
    </>
  );
}

function initSearchState(): SearchState {
  return {
    emailAccount: "",
    status: "" as MailboxAccountsQuery["status"],
    showStatusFilter: false,
  };
}

const useStyles = makeStyles({
  mainContainer: { display: "flex", gap: 20 },
  filterBtn: { boxShadow: "none" },
  statusFilterLabel: { marginBottom: "-4px" },
  filterPanel: {
    width: "fit-content",
    padding: "15px 20px 10px",
    borderRadius: 8,
  },
});
